import { Button, FileButton, Flex, ModalConfirmation, Spacing, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { DocumentStaticActions, DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'

import { ModalId } from '../../../../../../enums/modalId'
import { Scope } from '../../../../../../enums/scope'
import { isAuthorized } from '../../../../../../utils/isAuthorized'
import { MAX_FILE_SIZE_IN_BYTES } from '../../../../constants/uploadConstants'
import { useCreateFolderModal } from '../../../../contexts/CreateFolderModalContext'
import { useCopyDocumentOrFolderLink } from '../../../../hooks/useCopyDocumentOrFolderLink'
import { useDeleteCurrentFolder } from '../../hooks/useDeleteCurrentFolder'
import { FolderContentParams } from '../../types/folderContentParams'

const FOLDER_WRITE_ACTIONS = [
  DocumentStaticActionType.Rename,
  DocumentStaticActionType.Move,
  DocumentStaticActionType.CopyLink,
  DocumentStaticActionType.Delete,
]
const FOLDER_READ_ACTIONS = [DocumentStaticActionType.CopyLink]

interface FolderContentSideActionsProps {
  isUploading: boolean
  onFilesSelect: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => void
  onMove: () => void
  onRenameStart: () => void
}

export const FolderContentSideActions = ({
  isUploading,
  onFilesSelect,
  onMove,
  onRenameStart,
}: FolderContentSideActionsProps): ReactElement => {
  const { t } = useTranslation()
  const { open: openDeleteModal, close: closeDeleteModal } = useModal(ModalId.DocumentsFolderDeleteModal)
  const { folderId } = useParams<FolderContentParams>()
  const { openCreateFolderModal } = useCreateFolderModal()
  const { deleteCurrentFolder, isProcessing: isDeleting } = useDeleteCurrentFolder({ onSuccess: closeDeleteModal })
  const { copyLink } = useCopyDocumentOrFolderLink()
  const canWriteFolders = isAuthorized(Scope.DmsFolderWrite)
  const visibleActions = canWriteFolders ? FOLDER_WRITE_ACTIONS : FOLDER_READ_ACTIONS

  const handleCreateFolderModalOpen = useCallback(() => {
    openCreateFolderModal(folderId ?? null)
  }, [openCreateFolderModal, folderId])

  const handleStaticActionSelect = useCallback(
    (action: DocumentStaticActionType) => {
      switch (action) {
        case DocumentStaticActionType.Rename:
          onRenameStart()
          break
        case DocumentStaticActionType.Move:
          onMove()
          break
        case DocumentStaticActionType.CopyLink:
          if (folderId) {
            copyLink(folderId, 'folder')
          }
          break
        case DocumentStaticActionType.Delete:
          openDeleteModal()
          break
      }
    },
    [copyLink, folderId, onRenameStart, openDeleteModal, onMove],
  )

  return (
    <Flex alignItems="center" flexDirection="row" gap={Spacing.S8}>
      <DocumentStaticActions
        onSelect={handleStaticActionSelect}
        triggerVariant="outlined"
        visibleActions={visibleActions}
      />
      {canWriteFolders && (
        <Button icon="plus" onClick={handleCreateFolderModalOpen} variant="outlined">
          {t('documents.folder_view.create_folder')}
        </Button>
      )}
      <FileButton
        accept="all"
        icon="upload"
        loading={isUploading}
        maxSize={MAX_FILE_SIZE_IN_BYTES}
        multiple
        onFilesSelect={onFilesSelect}
      >
        {t('documents.folder_content.top_bar.upload')}
      </FileButton>
      <ModalConfirmation
        cancelLabel={t('cancel')}
        danger
        id={ModalId.DocumentsFolderDeleteModal}
        message={t('documents.file_list.delete_modal.folder.message')}
        okLabel={t('documents.file_list.delete_modal.delete_action')}
        okLoading={isDeleting}
        onCancel={closeDeleteModal}
        onOk={deleteCurrentFolder}
        title={t('documents.file_list.delete_modal.folder.title')}
      />
    </Flex>
  )
}
